import { Stack, Typography, Skeleton } from '@mui/material';
import React, { useMemo } from 'react';
import { useSourcingStats } from 'modules/sourcing-shared/hooks/useSourcingStats';
import { IconTooltip } from 'common/ui';

type Props = {
  totalSourcedCandidates: number;
  candidateBatchId?: string;
  organizationId: string;
};

type Stat = { value: string; label: string; tooltip: string };
export const SourcingStats: React.FC<Props> = ({
  totalSourcedCandidates,
  candidateBatchId,
  organizationId
}) => {
  const {
    appliedCount,
    deliveredPercentage,
    respondedPercentage,
    notInterestedPercentage,
    optedOutPercentage,
    isLoading
  } = useSourcingStats({
    candidateBatchId,
    organizationId,
    totalSourcedCandidates
  });

  const stats = useMemo<Stat[]>(() => {
    return [
      {
        value: `${totalSourcedCandidates}`,
        label: 'Invited',
        tooltip: 'Number of invitations sent'
      },
      {
        value: `${appliedCount}`,
        label: 'Applied',
        tooltip: 'Number of applications received'
      },
      {
        value: `${deliveredPercentage}%`,
        label: 'Delivered',
        tooltip: 'Percentage of candidates who received an invitation'
      },
      {
        value: `${respondedPercentage}%`,
        label: 'Responded',
        tooltip: 'Percentage of candidates who responded to the invitation'
      },
      {
        value: `${notInterestedPercentage}%`,
        label: 'Not interested',
        tooltip: 'Percentage of candidates who said they were not interested'
      },
      {
        value: `${optedOutPercentage}%`,
        label: 'Opted out',
        tooltip: 'Percentage of candidates who opted out'
      }
    ];
  }, [
    totalSourcedCandidates,
    appliedCount,
    deliveredPercentage,
    respondedPercentage,
    notInterestedPercentage,
    optedOutPercentage
  ]);

  if (isLoading) {
    return (
      <Stack direction="row" gap="17px" flex={1}>
        <Stack direction="row" alignItems="flex-start" flex={1}>
          {stats.map((stat, index) => (
            <Stack
              key={stat.label}
              direction="column"
              alignItems="flex-start"
              paddingTop="15px"
              paddingX="15px"
              borderTop="1px solid"
              borderColor={index < 2 ? 'primary.main' : 'customGrey.dark'}
              mr={index === 1 ? '17px' : 0}
              flex={1}
              gap="5px"
            >
              <Skeleton variant="text" width={60} height={36} />
              <Skeleton variant="text" width={80} height={24} />
            </Stack>
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap="17px" flex={1}>
      <Stack direction="row" alignItems="flex-start" flex={1}>
        {stats.map((stat, index) => (
          <Stack
            key={stat.label}
            direction="column"
            alignItems="flex-start"
            paddingTop="15px"
            paddingX="15px"
            borderTop="1px solid"
            borderColor={index < 2 ? 'primary.main' : 'customGrey.dark'}
            mr={index === 1 ? '17px' : 0}
            flex={1}
            gap="5px"
          >
            <IconTooltip title={stat.tooltip}>
              <Typography
                fontWeight={600}
                fontSize="24px"
                color="customGrey.dark"
              >
                {stat.value}
              </Typography>
            </IconTooltip>
            <Typography variant="body1" color="customGrey.dark">
              {stat.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
